import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Lock, Sparkles, Check, Zap, Clock, MessageCircle, Phone } from "lucide-react";
import TopBar from "@/components/TopBar";
import { useLanguage } from "@/i18n/LanguageContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";

const MILKADO_PHONE = "+243846103822";
const MILKADO_WHATSAPP = "243846103822";

const PAYMENT_METHODS = [
  { id: "mpesa",      label: "M-Pesa" },
  { id: "orange",     label: "Orange Money" },
  { id: "airtel",     label: "Airtel Money" },
  { id: "illicocash", label: "Illico Cash" },
  { id: "card",       label: "Carte bancaire" },
];

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?auto=format&fit=crop&w=900&q=70";

function formatRemaining(ms) {
  if (ms <= 0) return null;
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h >= 1) return `${h}h ${m.toString().padStart(2, "0")}min`;
  if (m >= 1) return `${m}min ${s.toString().padStart(2, "0")}s`;
  return `${s}s`;
}

export default function LastMinuteGift() {
  const { code, productId } = useParams();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  // Purchase drawer
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({
    contributor_name: "",
    contributor_phone: "",
    payment_method: "mpesa",
  });
  const [buyError, setBuyError] = useState("");
  const [buySubmitting, setBuySubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    api
      .get(`/public/last-minute-gifts/${code}/${productId}`)
      .then((r) => {
        setMeta(r.data);
        setBuyForm((f) => ({
          ...f,
          contributor_name: r.data.requester_first_name || "",
        }));
      })
      .catch((e) =>
        setError(formatApiError(e.response?.data?.detail) || "Lien invalide."),
      );
  }, [code, productId]);

  // Tick once per second for the countdown.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const remainingMs = useMemo(() => {
    if (!meta?.expires_at) return null;
    return new Date(meta.expires_at).getTime() - now;
  }, [meta, now]);

  const isExpired = meta?.expired || (remainingMs !== null && remainingMs <= 0);

  const submitBuy = async (e) => {
    e.preventDefault();
    setBuyError("");
    if (!buyForm.contributor_name.trim()) {
      setBuyError("Nom requis");
      return;
    }
    setBuySubmitting(true);
    try {
      await api.post("/public/contributions", {
        product_id: productId,
        event_code: code,
        contributor_name: buyForm.contributor_name.trim(),
        contributor_phone: buyForm.contributor_phone.trim(),
        payment_method: buyForm.payment_method,
        // SEC-002: amount + is_full_purchase are computed server-side.
        intent: "buy_full",
      });
      setSuccess(true);
      setBuyOpen(false);
    } catch (err) {
      setBuyError(
        formatApiError(err.response?.data?.detail) || "Erreur de paiement.",
      );
    } finally {
      setBuySubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-md mx-auto px-5 pt-12 text-center">
          <Lock className="w-10 h-10 text-[#D81B8C] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-black text-[#2A0E3C]">
            {error}
          </h1>
          <Button
            onClick={() => navigate("/")}
            className="mt-6 rounded-full bg-[#D81B8C] hover:bg-[#A60F6E]"
          >
            {t("common.back")}
          </Button>
        </div>
      </div>
    );
  }

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-md mx-auto px-5 pt-12 text-center text-[#5C4664]">
          {t("common.loading")}
        </div>
      </div>
    );
  }

  if (meta.purchased) {
    return (
      <div className="min-h-screen bg-[#FFF9FB]">
        <TopBar />
        <div className="max-w-md mx-auto px-5 pt-12 text-center">
          <Check className="w-12 h-12 text-[#3FA535] mx-auto mb-4" />
          <h1 className="font-display text-2xl font-black text-[#2A0E3C]">
            {t("lastMinute.alreadyPurchased")}
          </h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9FB]" data-testid="last-minute-page">
      <TopBar />
      <div className="max-w-md mx-auto px-5 pt-6 pb-24">
        <button
          onClick={() => navigate(`/event/${code}`)}
          className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C]"
          data-testid="lm-back"
        >
          <ArrowLeft className="w-4 h-4" /> {t("common.back")}
        </button>

        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full bg-[#FFE9B0] text-[#9A6500] text-[11px] font-bold uppercase tracking-widest">
            <Zap className="w-3.5 h-3.5" /> {t("lastMinute.pageTitle")}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C] mt-3 leading-tight">
            {t("lastMinute.greeting").replace(
              "{name}",
              meta.requester_first_name || "",
            )}
          </h1>
          <p className="mt-2 text-[#5C4664] text-sm">{meta.event_title}</p>
        </div>

        {/* Product card */}
        <div className="mt-7 rounded-3xl bg-white border border-[#F2E2EC] overflow-hidden">
          <div
            className="aspect-[4/3] bg-center bg-cover"
            style={{
              backgroundImage: `url(${meta.product.image_url || FALLBACK_IMG})`,
            }}
          />
          <div className="p-5">
            <h2 className="font-display text-xl font-black text-[#2A0E3C] leading-tight">
              {meta.product.name}
            </h2>
            <p className="mt-1 text-xs text-[#5C4664]">{meta.product.category}</p>
            <div className="mt-3 font-display text-2xl font-black text-[#D81B8C]">
              ${(meta.product.final_price ?? meta.product.price).toFixed(0)}
            </div>
            <div className="text-[10px] text-[#5C4664] mt-0.5">
              {t("common2.allInclusive")}
            </div>
          </div>
        </div>

        {/* Expired state */}
        {isExpired && !success && (
          <div
            data-testid="lm-expired-block"
            className="mt-6 rounded-2xl border border-[#F2E2EC] bg-white p-5"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FCE7F3] text-[#D81B8C] flex items-center justify-center shrink-0">
                <Lock className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg font-black text-[#2A0E3C]">
                  {t("lastMinute.expiredTitle")}
                </h3>
                <p className="mt-1 text-sm text-[#5C4664] leading-relaxed">
                  {t("lastMinute.expiredBody")}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={`https://wa.me/${MILKADO_WHATSAPP}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="lm-expired-wa"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#3FA535] hover:bg-[#2E7E27] text-white px-4 h-10 text-sm font-semibold"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                  <a
                    href={`tel:${MILKADO_PHONE}`}
                    data-testid="lm-expired-tel"
                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#F2E2EC] hover:border-[#D81B8C] text-[#2A0E3C] px-4 h-10 text-sm font-semibold"
                  >
                    <Phone className="w-4 h-4 text-[#D81B8C]" /> Appeler
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Buy CTA + discreet countdown */}
        {!isExpired && !success && (
          <div className="mt-6">
            <Button
              onClick={() => setBuyOpen(true)}
              data-testid="lm-buy-btn"
              className="milk-shimmer w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-12 font-semibold gap-2"
            >
              <Sparkles className="w-4 h-4" /> {t("lastMinute.buyNow")}
            </Button>

            {remainingMs !== null && remainingMs > 0 && (
              <div
                data-testid="lm-countdown"
                className="mt-3 flex items-center justify-center gap-1.5 text-xs text-[#5C4664]"
              >
                <Clock className="w-3.5 h-3.5" />
                {t("lastMinute.countdownPrefix")}{" "}
                <span className="font-semibold text-[#2A0E3C]">
                  {formatRemaining(remainingMs)}
                </span>{" "}
                {t("lastMinute.countdownSuffix")}
              </div>
            )}
          </div>
        )}

        {success && (
          <div
            data-testid="lm-success-block"
            className="mt-8 p-6 rounded-2xl bg-[#E8F6E5] border border-[#3FA535]/40 text-center"
          >
            <Check className="w-10 h-10 text-[#3FA535] mx-auto mb-2" />
            <h3 className="font-display text-xl font-black text-[#2A0E3C]">
              {t("lastMinute.paySuccess")}
            </h3>
            <p className="mt-1 text-sm text-[#5C4664]">
              {t("lastMinute.paySuccessSub")}
            </p>
          </div>
        )}
      </div>

      {/* Buy drawer */}
      <Drawer open={buyOpen} onOpenChange={setBuyOpen}>
        <DrawerContent className="bg-white max-h-[92vh]">
          <div className="max-w-md mx-auto w-full px-5 pb-8">
            <DrawerHeader className="px-0">
              <DrawerTitle className="font-display text-2xl font-black text-[#2A0E3C]">
                {t("lastMinute.buyNow")}
              </DrawerTitle>
              <DrawerDescription className="text-[#5C4664]">
                {meta.product.name} — ${(meta.product.final_price ?? meta.product.price).toFixed(0)}
              </DrawerDescription>
            </DrawerHeader>

            <form onSubmit={submitBuy} className="space-y-4">
              <div>
                <Label>Nom complet</Label>
                <Input
                  data-testid="lm-buy-name"
                  value={buyForm.contributor_name}
                  onChange={(e) =>
                    setBuyForm({ ...buyForm, contributor_name: e.target.value })
                  }
                  className="mt-1"
                  required
                />
              </div>
              <div>
                <Label>Téléphone (optionnel)</Label>
                <Input
                  data-testid="lm-buy-phone"
                  value={buyForm.contributor_phone}
                  onChange={(e) =>
                    setBuyForm({ ...buyForm, contributor_phone: e.target.value })
                  }
                  placeholder="+243 …"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>{t("lastMinute.payIn")}</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((m) => {
                    const active = buyForm.payment_method === m.id;
                    return (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`lm-pay-${m.id}`}
                        onClick={() =>
                          setBuyForm({ ...buyForm, payment_method: m.id })
                        }
                        className={`px-3 h-11 rounded-xl text-sm font-semibold border transition-all ${
                          active
                            ? "border-[#D81B8C] bg-[#FCE7F3] text-[#2A0E3C]"
                            : "border-[#F2E2EC] hover:border-[#D81B8C]/50"
                        }`}
                      >
                        {m.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {buyError && (
                <p
                  data-testid="lm-buy-error"
                  className="text-sm text-[#D81B8C] font-medium"
                >
                  {buyError}
                </p>
              )}

              <Button
                type="submit"
                disabled={buySubmitting}
                data-testid="lm-buy-submit"
                className="w-full rounded-full bg-[#D81B8C] hover:bg-[#A60F6E] text-white h-12 font-semibold disabled:opacity-60"
              >
                {buySubmitting ? "…" : `${t("lastMinute.payIn")} $${(meta.product.final_price ?? meta.product.price).toFixed(0)}`}
              </Button>
            </form>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
